/**
 * Events API client for Stage Door Access
 */

import { throwApiError } from './client-error';
import { getAttribution } from '../lib/attribution';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

/**
 * Event/Tour Stop types
 */
export interface TourStop {
  id: string;
  show_id: string;
  theater_id: string;
  city_id: string;
  start_date: string;
  end_date: string;
  status: 'scheduled' | 'on_sale' | 'sold_out' | 'cancelled';
  show: {
    id: string;
    name: string;
    description: string;
    logo_url: string | null;
    is_active: boolean;
  };
  theater: {
    id: string;
    name: string;
    address: string;
    latitude: number;
    longitude: number;
  };
  city: {
    id: string;
    name: string;
    state: string;
  };
  straight_line_miles?: number;
  distance_miles?: number;
  date_view_mode?: 'auto' | 'list' | 'calendar';
  matinee_time?: string | null;
}

export interface Show {
  id: string;
  name: string;
  description: string;
  logo_url: string | null;
  is_active: boolean;
  active_tour_stops: number;
}

export interface ClassOption {
  id: string;
  show_id: string;
  name: string;
  duration_minutes: number;
  max_students: number;
  base_price_cents: number;
  is_active: boolean;
}

export interface PricingTier {
  tier: 'standard' | 'tier_1' | 'tier_2' | 'dual_option' | 'hosted';
  surcharge_cents: number;
  description: string;
}

export interface EventPricingResponse {
  tour_stop_id: string;
  distance_miles: number;
  pricing_tier: PricingTier;
  class_options: {
    id: string;
    name: string;
    duration_minutes: number;
    max_students: number;
    base_price_cents: number;
    travel_surcharge_cents: number;
    total_price_cents: number;
  }[];
  available_paths: ('direct_booking' | 'hosted_inquiry' | 'remote_inquiry')[];
  // Geocoded origin coords (from the studio's zip). Useful for client-side
  // haversine distance against other tour stops without N pricing calls.
  origin_coords?: { lat: number; lng: number };
  alternative_stops?: {
    tour_stop_id: string;
    show_name: string;
    venue_name: string;
    city: string;
    state: string;
    distance_miles: number;
    start_date: string;
    end_date: string;
    starting_price_cents: number;
    travel_surcharge_cents: number;
  }[];
  using_straight_line: boolean;
}

/**
 * Session identifiers — semantic slot names decoupled from hardcoded times.
 * Actual start/end times are resolved dynamically from system settings.
 */
export type SessionIdentifier = 'session_1' | 'session_2';
export type SessionSelection = SessionIdentifier | 'both';
export const SESSION_IDENTIFIERS: SessionIdentifier[] = ['session_1', 'session_2'];

/**
 * @deprecated Use SessionIdentifier instead. Kept for backward compatibility.
 */
export type ClassTimeSlot = '9:00 AM' | '11:00 AM';
export type TimeSlotSelection = ClassTimeSlot | SessionIdentifier | 'both';

export interface TimeSlot {
  date: string;
  time_slot: ClassTimeSlot | string; // ClassTimeSlot for Sat/Sun; arbitrary time strings for Monday multi-slot
  available: boolean;
  reason?: string;
  remaining: number;
  total: number;
  sold_out: boolean;
  day_type?: 'saturday' | 'sunday' | 'monday';
  /** Raw start_time from API (HH:MM:SS), used for Monday slot display */
  start_time?: string;
  /** Raw end_time from API (HH:MM:SS), used for Monday slot display */
  end_time?: string;
  /** Date is within the closed booking window (e.g. 21 days from today) */
  is_closed?: boolean;
}

/**
 * @deprecated Use SESSION_IDENTIFIERS instead.
 */
export const CLASS_TIME_SLOTS: ClassTimeSlot[] = ['9:00 AM', '11:00 AM'];

/**
 * Public system settings for the booking flow
 */
export interface PublicSystemSettings {
  default_matinee_time: string;
  matinee_threshold_time: string;
  default_session_start_time: string;
  session_duration_minutes: number;
}

/**
 * Fetch public system settings (no auth required)
 */
export async function getPublicSystemSettings(): Promise<PublicSystemSettings> {
  const response = await fetch(`${API_BASE_URL}/api/events/settings`);
  const result = await response.json();
  if (!response.ok) throwApiError(response, result, 'Failed to fetch settings');
  return result.data;
}

/**
 * Search for events by location (ZIP, coordinates, or address)
 */
export async function searchEvents(params: {
  zip?: string;
  lat?: number;
  lng?: number;
  radius?: number;
  show_id?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  per_page?: number;
}): Promise<{
  data: TourStop[];
  meta: { page: number; per_page: number; total: number; total_pages: number };
}> {
  const searchParams = new URLSearchParams();
  if (params.zip) searchParams.set('zip', params.zip);
  if (params.lat != null) searchParams.set('lat', params.lat.toString());
  if (params.lng != null) searchParams.set('lng', params.lng.toString());
  if (params.radius) searchParams.set('radius', params.radius.toString());
  if (params.show_id) searchParams.set('show_id', params.show_id);
  if (params.start_date) searchParams.set('start_date', params.start_date);
  if (params.end_date) searchParams.set('end_date', params.end_date);
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.per_page) searchParams.set('per_page', params.per_page.toString());

  const response = await fetch(`${API_BASE_URL}/api/events?${searchParams}`);
  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to search events');
  }

  return result;
}

/**
 * Get event details
 */
export async function getEventDetails(tourStopId: string): Promise<TourStop & { class_options: ClassOption[] }> {
  const response = await fetch(`${API_BASE_URL}/api/events/${tourStopId}`);
  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to get event details');
  }

  return result.data;
}

/**
 * Get pricing for an event based on studio location
 */
export async function getEventPricing(
  tourStopId: string,
  location: { zip?: string; lat?: number; lng?: number }
): Promise<EventPricingResponse> {
  const params = new URLSearchParams();
  if (location.zip) params.set('zip', location.zip);
  if (location.lat != null) params.set('lat', location.lat.toString());
  if (location.lng != null) params.set('lng', location.lng.toString());

  const response = await fetch(`${API_BASE_URL}/api/events/${tourStopId}/pricing?${params}`);
  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to get pricing');
  }

  return result.data;
}

/**
 * Format a Monday slot's start_time (HH:MM:SS) into a human-readable key like "9:00 AM".
 * Used as time_slot identifier for Monday multi-slot grid.
 */
function formatMondayTimeSlotKey(startTime: string): string {
  const parts = startTime.split(':');
  const hours = parseInt(parts[0] || '0', 10);
  const minutes = parseInt(parts[1] || '0', 10);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMinutes = String(minutes).padStart(2, '0');
  return `${displayHours}:${displayMinutes} ${period}`;
}

/**
 * Helper to map API start time to standard class time slot
 */
function mapToClassTimeSlot(startTime: string): ClassTimeSlot | null {
  const hour = parseInt(startTime.split(':')[0] || '0', 10);
  // 9:00 AM slot (8-10 range)
  if (hour >= 8 && hour < 10) return '9:00 AM';
  // 11:00 AM slot (10-12 range)
  if (hour >= 10 && hour < 13) return '11:00 AM';
  return null; // Not a standard time slot
}

/**
 * Format a date as YYYY-MM-DD in local timezone
 * This avoids timezone issues that occur with toISOString()
 */
function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get the next Saturday from a given date (inclusive)
 */
function getNextSaturday(date: Date): Date {
  const result = new Date(date);
  const dayOfWeek = result.getDay();
  const daysUntilSaturday = dayOfWeek === 6 ? 0 : (6 - dayOfWeek);
  result.setDate(result.getDate() + daysUntilSaturday);
  return result;
}

/**
 * Get availability for an event
 * Business Rules:
 * - Classes available Saturday, Sunday & Monday
 * - Two time slots per day (Session 1 + Session 2, 90 min each)
 * - Only show dates from today onwards
 */
export type DateViewMode = 'auto' | 'list' | 'calendar';

export interface DayTimeConfig {
  saturday_matinee_time: string | null;
  sunday_matinee_time: string;
  monday_start_time: string;
}

export interface EventAvailabilityResult {
  slots: TimeSlot[];
  dateViewMode: DateViewMode;
  dayTimeConfig: DayTimeConfig | null;
}

/**
 * Determine the day_type from a Date object's day-of-week.
 */
function getDayType(date: Date): 'saturday' | 'sunday' | 'monday' | null {
  const dow = date.getDay();
  if (dow === 6) return 'saturday';
  if (dow === 0) return 'sunday';
  if (dow === 1) return 'monday';
  return null;
}

/**
 * Check if a date is a valid class day (Sat, Sun, or Mon).
 */
function isClassDay(date: Date): boolean {
  return getDayType(date) !== null;
}

export async function getEventAvailability(tourStopId: string): Promise<EventAvailabilityResult> {
  const response = await fetch(`${API_BASE_URL}/api/events/${tourStopId}/availability`);
  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to get availability');
  }

  // Transform the API response (grouped by date) into flat TimeSlot[] array
  const data = result.data as {
    tour_stop_id: string;
    tour_dates: { start: string; end: string };
    date_view_mode?: DateViewMode;
    day_time_config?: DayTimeConfig;
    remaining?: number;
    total?: number;
    sold_out?: boolean;
    availability: Record<string, Array<{
      start_time: string;
      end_time: string;
      status: string;
      class_id?: string;
      class_option_id?: string;
      remaining?: number;
      total?: number;
      sold_out?: boolean;
    }> | {
      remaining?: number;
      total?: number;
      available_faculty?: number;
      sold_out?: boolean;
      day_type?: 'saturday' | 'sunday' | 'monday';
      slots: Array<{
        start_time: string;
        end_time: string;
        status: string;
        class_id?: string;
        class_option_id?: string;
      }>;
    }>;
  };

  const dateViewMode: DateViewMode = data.date_view_mode || 'auto';

  const timeSlots: TimeSlot[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = formatLocalDate(today);

  // Parse tour dates
  const tourStartDate = new Date(data.tour_dates.start + 'T00:00:00');
  const tourEndDate = new Date(data.tour_dates.end + 'T00:00:00');

  // Determine the effective start date (later of tour start or today)
  const effectiveStartDate = tourStartDate > today ? tourStartDate : today;

  // Find the first Saturday on or after the effective start date
  const firstSaturday = getNextSaturday(effectiveStartDate);

  // Default capacity from top-level response
  const defaultTotal = data.total ?? 3;
  const defaultRemaining = data.remaining ?? defaultTotal;
  const defaultSoldOut = data.sold_out ?? false;

  // If no availability data in DB, generate Saturday-only slots for tour date range (legacy fallback)
  if (!data.availability || Object.keys(data.availability).length === 0) {
    // Generate slots for all Saturdays from firstSaturday to tour end date
    for (let d = new Date(firstSaturday); d <= tourEndDate; d.setDate(d.getDate() + 7)) {
      const dateStr = formatLocalDate(d);
      CLASS_TIME_SLOTS.forEach((slot) => {
        timeSlots.push({
          date: dateStr,
          time_slot: slot,
          available: !defaultSoldOut && defaultRemaining > 0,
          remaining: defaultRemaining,
          total: defaultTotal,
          sold_out: defaultSoldOut,
          day_type: 'saturday',
        });
      });
    }
    return { slots: timeSlots, dateViewMode, dayTimeConfig: data.day_time_config || null };
  }

  // Transform existing availability data (Sat/Sun/Mon, only future dates)
  for (const [date, dateValue] of Object.entries(data.availability)) {
    // Skip past dates
    if (date < todayStr) continue;

    const dateObj = new Date(date + 'T00:00:00');

    // Handle both old format (flat array) and new format (object with .slots)
    const isNewFormat = !Array.isArray(dateValue) && dateValue && typeof dateValue === 'object' && 'slots' in dateValue;
    const slots = isNewFormat ? (dateValue as { slots: Array<{ start_time: string; end_time: string; status: string; class_id?: string; class_option_id?: string }> }).slots : (dateValue as Array<{ start_time: string; end_time: string; status: string; class_id?: string; class_option_id?: string; remaining?: number; total?: number; sold_out?: boolean }>);
    const dateRemaining = isNewFormat ? ((dateValue as { remaining?: number }).remaining ?? defaultRemaining) : defaultRemaining;
    const dateTotal = isNewFormat ? ((dateValue as { total?: number }).total ?? defaultTotal) : defaultTotal;
    const dateSoldOut = isNewFormat ? ((dateValue as { sold_out?: boolean }).sold_out ?? false) : defaultSoldOut;
    const isClosed = isNewFormat ? ((dateValue as { is_closed?: boolean }).is_closed ?? false) : false;

    // Resolve day_type: prefer API value, fall back to computing from date
    const apiDayType = isNewFormat ? (dateValue as { day_type?: 'saturday' | 'sunday' | 'monday' }).day_type : undefined;
    const dayType = apiDayType ?? getDayType(dateObj) ?? 'saturday';

    // Skip dates that are not valid class days (only if no API day_type)
    if (!apiDayType && !isClassDay(dateObj)) continue;

    // Monday: each API slot becomes its own TimeSlot entry (multi-slot grid)
    if (dayType === 'monday') {
      for (const slot of slots) {
        const slotRemaining = (slot as { remaining?: number }).remaining ?? dateRemaining;
        const slotTotal = (slot as { total?: number }).total ?? dateTotal;
        const slotSoldOut = (slot as { sold_out?: boolean }).sold_out ?? dateSoldOut;
        // Use formatted start time as the time_slot key for Monday
        const formattedStart = formatMondayTimeSlotKey(slot.start_time);
        timeSlots.push({
          date,
          time_slot: formattedStart,
          available: (slot.status === 'available' || slot.status === 'open') && !slotSoldOut && !isClosed,
          reason: slot.status !== 'available' && slot.status !== 'open'
            ? `Status: ${slot.status}`
            : undefined,
          remaining: slotRemaining,
          total: slotTotal,
          sold_out: slotSoldOut,
          day_type: dayType,
          start_time: slot.start_time,
          end_time: slot.end_time,
          is_closed: isClosed,
        });
      }
    } else {
      // Saturday/Sunday: collapse into Session 1 / Session 2 buckets
      const addedSlots = new Set<ClassTimeSlot>();

      for (const slot of slots) {
        const timeSlot = mapToClassTimeSlot(slot.start_time);
        if (!timeSlot) continue; // Skip non-standard times

        // Only add one entry per time slot per date
        if (!addedSlots.has(timeSlot)) {
          addedSlots.add(timeSlot);
          const slotRemaining = (slot as { remaining?: number }).remaining ?? dateRemaining;
          const slotTotal = (slot as { total?: number }).total ?? dateTotal;
          const slotSoldOut = (slot as { sold_out?: boolean }).sold_out ?? dateSoldOut;
          timeSlots.push({
            date,
            time_slot: timeSlot,
            available: (slot.status === 'available' || slot.status === 'open') && !slotSoldOut && !isClosed,
            reason: slot.status !== 'available' && slot.status !== 'open'
              ? `Status: ${slot.status}`
              : undefined,
            remaining: slotRemaining,
            total: slotTotal,
            sold_out: slotSoldOut,
            day_type: dayType,
            start_time: slot.start_time,
            end_time: slot.end_time,
            is_closed: isClosed,
          });
        }
      }

      // If we have a date in the data but missing a time slot, use date-level status
      for (const slot of CLASS_TIME_SLOTS) {
        if (!addedSlots.has(slot)) {
          timeSlots.push({
            date,
            time_slot: slot,
            available: !dateSoldOut && dateRemaining > 0 && !isClosed,
            remaining: dateRemaining,
            total: dateTotal,
            sold_out: dateSoldOut,
            day_type: dayType,
            is_closed: isClosed,
          });
        }
      }
    }
  }

  // Fill in any Saturdays within the tour range that had no DB records (legacy fallback)
  const datesWithData = new Set(Object.keys(data.availability));
  const bookingCutoffDays = 21;
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() + bookingCutoffDays);
  cutoffDate.setHours(0, 0, 0, 0);

  for (let d = new Date(firstSaturday); d <= tourEndDate; d.setDate(d.getDate() + 7)) {
    const dateStr = formatLocalDate(d);
    if (dateStr < todayStr) continue;
    if (datesWithData.has(dateStr)) continue; // Already processed from DB
    const legacyClosed = d <= cutoffDate;
    CLASS_TIME_SLOTS.forEach((slot) => {
      timeSlots.push({
        date: dateStr,
        time_slot: slot,
        available: !defaultSoldOut && defaultRemaining > 0 && !legacyClosed,
        remaining: defaultRemaining,
        total: defaultTotal,
        sold_out: defaultSoldOut,
        day_type: 'saturday',
        is_closed: legacyClosed,
      });
    });
  }

  // Sort by date, then by time slot order
  const knownSlotOrder: Record<string, number> = { '9:00 AM': 0, '11:00 AM': 1 };
  timeSlots.sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    // Use start_time for Monday slots (chronological), known order for Sat/Sun
    if (a.start_time && b.start_time) {
      return a.start_time.localeCompare(b.start_time);
    }
    return (knownSlotOrder[a.time_slot] ?? 99) - (knownSlotOrder[b.time_slot] ?? 99);
  });

  return { slots: timeSlots, dateViewMode, dayTimeConfig: data.day_time_config || null };
}

/**
 * Join waitlist for a sold-out event
 */
export async function joinWaitlist(tourStopId: string, data: {
  email: string;
  studio_name?: string;
  contact_name?: string;
  phone?: string;
  class_option_id?: string;
  preferred_date?: string;
  preferred_time_slot?: ClassTimeSlot;
  notes?: string;
}, token?: string | null): Promise<{ position: number }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(`${API_BASE_URL}/api/events/${tourStopId}/waitlist`, {
    method: 'POST',
    headers,
    // SDA-267: attach stored campaign attribution out-of-band; the server
    // validates and stamps it (absent/invalid never blocks the waitlist join).
    body: JSON.stringify({ ...data, attribution: getAttribution() ?? undefined }),
  });
  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to join waitlist');
  }

  return result.data;
}

/**
 * Subscribe to interest notifications for a show (public, no auth required)
 */
export async function subscribeInterest(data: {
  email: string;
  name: string;
  show_id: string;
}): Promise<{ duplicate: boolean }> {
  const response = await fetch(`${API_BASE_URL}/api/interest/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const result = await response.json();
  // 409 = already subscribed — not an error
  if (response.status === 409) {
    return { duplicate: true };
  }
  if (!result.success) {
    throwApiError(response, result, 'Subscription failed');
  }
  return { duplicate: false };
}

// =============================================================================
// Admin Waitlist Types & API
// =============================================================================

export interface WaitlistEntry {
  id: string;
  email: string;
  phone: string | null;
  contact_name: string | null;
  studio_name: string | null;
  show_name: string | null;
  tour_stop_label: string | null;
  preferred_date: string | null;
  position: number;
  notified_at: string | null;
  converted_at: string | null;
  created_at: string;
}

/** Result of a Notify action — the API reports whether the email actually sent. */
export interface WaitlistNotifyResult {
  email_sent: boolean;
  reason?: 'no_email_account' | 'send_failed' | 'no_recipient';
}

export interface WaitlistSummary {
  total: number;
  by_show: { show_name: string; count: number }[];
}

export interface AdminWaitlistResponse {
  data: WaitlistEntry[];
  pagination: { page: number; per_page: number; total: number };
  summary: WaitlistSummary;
}

export async function getAdminWaitlist(params?: {
  show_id?: string;
  tour_stop_id?: string;
  status?: string;
  page?: number;
  per_page?: number;
}): Promise<AdminWaitlistResponse> {
  const { authenticatedFetch } = await import('./auth');
  const query = new URLSearchParams();
  if (params?.show_id) query.set('show_id', params.show_id);
  if (params?.tour_stop_id) query.set('tour_stop_id', params.tour_stop_id);
  if (params?.status) query.set('status', params.status);
  if (params?.page) query.set('page', String(params.page));
  if (params?.per_page) query.set('per_page', String(params.per_page));

  const response = await authenticatedFetch(`${API_BASE_URL}/api/admin/waitlist?${query.toString()}`);
  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to load waitlist');
  }

  return result.data;
}

export async function deleteWaitlistEntry(id: string): Promise<void> {
  const { authenticatedFetch } = await import('./auth');
  const response = await authenticatedFetch(`${API_BASE_URL}/api/admin/waitlist/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const result = await response.json();
    throwApiError(response, result, 'Failed to delete waitlist entry');
  }
}

export async function notifyWaitlistEntry(id: string): Promise<WaitlistEntry & WaitlistNotifyResult> {
  const { authenticatedFetch } = await import('./auth');
  const response = await authenticatedFetch(`${API_BASE_URL}/api/admin/waitlist/${id}/notify`, {
    method: 'POST',
  });
  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to notify waitlist entry');
  }

  return result.data;
}

/**
 * Get list of all shows
 */
export async function getShows(): Promise<Show[]> {
  const response = await fetch(`${API_BASE_URL}/api/events/shows`);
  const result = await response.json();

  if (!response.ok) {
    throwApiError(response, result, 'Failed to get shows');
  }

  return result.data;
}

/**
 * Format price in cents to display string
 */
export function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/**
 * Format date range
 */
export function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

  if (start.getFullYear() !== end.getFullYear()) {
    return `${start.toLocaleDateString('en-US', { ...options, year: 'numeric' })} - ${end.toLocaleDateString('en-US', { ...options, year: 'numeric' })}`;
  }

  if (start.getMonth() === end.getMonth()) {
    return `${start.toLocaleDateString('en-US', { month: 'short' })} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
  }

  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}, ${start.getFullYear()}`;
}
